// Patienten-Liste + Detail-View für die Praxis.
//
// Zeigt alle Patienten, die sich per QR-Scan mit dieser Praxis verknüpft haben.
// Beim Klick auf einen Patienten: Detailansicht mit:
//   - Stammdaten (Name, GebDat, E-Mail, Consent-Datum)
//   - Krankmeldungs-Historie
//   - Q&A-Threads
//   - Buttons: "Termin-Ruf senden" (Push) + "Akte sperren" (DSGVO)
//
// Datenschutz-Hinweis im UI: die Praxis sieht NUR ihre eigenen Patienten.

import { useEffect, useState } from 'react';
import AuthImage from '../components/AuthImage';
import {
  getPatient,
  listPatienten,
  revokePatient,
  terminRufPatient,
  updatePatient,
  vorschlagTermin,
  type FrageThread,
  type KrankmeldungAnfrage,
  type PatientPublic,
} from '../api';

export default function Patienten() {
  const [patienten, setPatienten] = useState<PatientPublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await listPatienten();
      setPatienten(r.patienten);
    } catch (err: any) {
      alert('Fehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const filtered = patienten.filter((p) => {
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return (
      p.vorname.toLowerCase().includes(q) ||
      p.nachname.toLowerCase().includes(q) ||
      p.email.toLowerCase().includes(q)
    );
  });

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Patienten</h1>
          <p className="muted">Alle Patienten, die sich per QR-Scan mit Ihrer Praxis verknüpft haben. Daten sind verschlüsselt gespeichert.</p>
        </div>
        <button className="btn-secondary" onClick={reload}>↻ Neu laden</button>
      </div>

      <div className="toolbar">
        <input
          className="search"
          placeholder="Name oder E-Mail suchen…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <div className="muted small">
          {loading ? 'Lädt…' : `${filtered.length} von ${patienten.length}`}
        </div>
      </div>

      {patienten.length === 0 && !loading ? (
        <div className="empty-card">
          <div className="empty-icon">👥</div>
          <h3>Noch keine Patienten</h3>
          <p>Sobald sich Patienten per QR-Code mit Ihrer Praxis verknüpfen, erscheinen sie hier.</p>
        </div>
      ) : (
        <div className="patienten-grid">
          {filtered.map((p) => (
            <button
              key={p.id}
              className={`patient-card ${selected === p.id ? 'selected' : ''}`}
              onClick={() => setSelected(p.id)}
            >
              <div className="patient-name">{p.vorname} {p.nachname}</div>
              <div className="patient-meta">
                <span>🎂 {p.geburtsdatum}</span>
                {p.has_push && <span title="Push-Notifications aktiv">📱</span>}
              </div>
              <div className="patient-email">{p.email}</div>
              <div className="patient-foot muted small">
                Zuletzt aktiv: {formatRelative(p.last_seen_at)}
              </div>
            </button>
          ))}
        </div>
      )}

      {selected && (
        <PatientDetailModal
          patientId={selected}
          onClose={() => setSelected(null)}
          onRevoked={() => { setSelected(null); reload(); }}
        />
      )}
    </div>
  );
}

function PatientEditForm({
  patient,
  onClose,
  onSaved,
}: {
  patient: PatientPublic;
  onClose: () => void;
  onSaved: (updated: PatientPublic) => void;
}) {
  const [vorname, setVorname] = useState(patient.vorname);
  const [nachname, setNachname] = useState(patient.nachname);
  const [geburtsdatum, setGeburtsdatum] = useState(patient.geburtsdatum);
  const [email, setEmail] = useState(patient.email);
  const [notiz, setNotiz] = useState(patient.praxis_notiz ?? '');
  const [chatBanned, setChatBanned] = useState(Boolean(patient.chat_banned));
  const [banReason, setBanReason] = useState(patient.chat_ban_reason ?? '');
  const [saving, setSaving] = useState(false);

  const validateBirthdate = (s: string): string | null => {
    const m = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(s.trim());
    if (!m) return 'Format: TT.MM.JJJJ';
    const day = Number(m[1]), month = Number(m[2]), year = Number(m[3]);
    if (month < 1 || month > 12) return `Monat ungültig: ${month}`;
    if (day < 1 || day > 31) return `Tag ungültig: ${day}`;
    const d = new Date(year, month - 1, day);
    if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return 'Dieses Datum gibt es nicht';
    return null;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const bErr = validateBirthdate(geburtsdatum);
    if (bErr) { alert('Geburtsdatum: ' + bErr); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { alert('E-Mail ungültig'); return; }
    setSaving(true);
    try {
      const r = await updatePatient(patient.id, {
        chat_banned: chatBanned,
        chat_ban_reason: chatBanned ? (banReason || null) : null,
        praxis_notiz: notiz || null,
        pii: {
          vorname: vorname.trim(),
          nachname: nachname.trim(),
          geburtsdatum: geburtsdatum.trim(),
          email: email.trim(),
        },
      });
      onSaved(r.patient);
    } catch (err: any) {
      alert('Fehler: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-card" style={{ maxWidth: 540 }}>
        <div className="modal-header">
          <h2 style={{ margin: 0 }}>Patient bearbeiten</h2>
          <button className="btn-icon" onClick={onClose}>×</button>
        </div>
        <form onSubmit={onSubmit} className="modal-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="field">
              <label>Vorname</label>
              <input type="text" value={vorname} onChange={(e) => setVorname(e.target.value)} required />
            </div>
            <div className="field">
              <label>Nachname</label>
              <input type="text" value={nachname} onChange={(e) => setNachname(e.target.value)} required />
            </div>
            <div className="field">
              <label>Geburtsdatum (TT.MM.JJJJ)</label>
              <input type="text" value={geburtsdatum} onChange={(e) => setGeburtsdatum(e.target.value)} required placeholder="01.01.1985" />
            </div>
            <div className="field">
              <label>E-Mail</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
          </div>
          <div className="field">
            <label>Praxis-Notiz (intern, nur Sie sehen das)</label>
            <textarea
              rows={3}
              value={notiz}
              onChange={(e) => setNotiz(e.target.value)}
              maxLength={2000}
              placeholder="z. B. Allergien, Vorerkrankungen, Besonderheiten"
            />
          </div>

          <h4 className="section-title" style={{ marginTop: 16 }}>Chat-Sperre</h4>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={chatBanned}
              onChange={(e) => setChatBanned(e.target.checked)}
              style={{ width: 18, height: 18 }}
            />
            <span style={{ fontSize: 14 }}>Patient vom Chat sperren</span>
          </label>
          {chatBanned && (
            <div className="field">
              <label>Grund (wird Patient angezeigt)</label>
              <input
                type="text"
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                placeholder="z. B. Wiederholter Missbrauch der Chat-Funktion"
                maxLength={500}
              />
            </div>
          )}

          <div className="row" style={{ justifyContent: 'flex-end', marginTop: 12 }}>
            <button type="button" className="btn-secondary" onClick={onClose}>Abbrechen</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Speichern…' : 'Änderungen speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PatientTerminForm({
  patientId,
  patientName,
  onClose,
  onSent,
}: {
  patientId: string;
  patientName: string;
  onClose: () => void;
  onSent: () => void;
}) {
  const tomorrow = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); })();
  const [datum, setDatum] = useState(tomorrow);
  const [uhrzeit, setUhrzeit] = useState('09:00');
  const [notiz, setNotiz] = useState('');
  const [sending, setSending] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    try {
      await vorschlagTermin(patientId, { termin_datum: datum, termin_uhrzeit: uhrzeit, notiz: notiz.trim() || undefined });
      alert(`Vorschlag an ${patientName} gesendet.`);
      onSent();
    } catch (err: any) {
      alert('Fehler: ' + err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={onSubmit} style={{
      background: '#FFF4E5', borderRadius: 8, padding: 12, marginTop: 12,
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#B85C00' }}>Termin vorschlagen für {patientName}</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input
          type="date"
          value={datum}
          min={new Date().toISOString().slice(0, 10)}
          onChange={(e) => setDatum(e.target.value)}
          required
          style={{ padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6 }}
        />
        <input
          type="time"
          value={uhrzeit}
          onChange={(e) => setUhrzeit(e.target.value)}
          required
          style={{ padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6 }}
        />
      </div>
      <textarea
        placeholder="Notiz (optional, z. B. Grund für den Termin)"
        value={notiz}
        onChange={(e) => setNotiz(e.target.value)}
        maxLength={1000}
        rows={2}
        style={{ padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, fontFamily: 'inherit' }}
      />
      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <button type="button" className="btn-secondary" onClick={onClose}>Abbrechen</button>
        <button type="submit" className="btn-primary" disabled={sending}>
          {sending ? 'Sende…' : 'Vorschlag senden'}
        </button>
      </div>
    </form>
  );
}

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `vor ${mins} Min.`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `vor ${h} Std.`;
  const d = Math.floor(h / 24);
  if (d < 7) return `vor ${d} Tagen`;
  return new Date(iso).toLocaleDateString('de-DE');
}

function PatientDetailModal({
  patientId,
  onClose,
  onRevoked,
}: {
  patientId: string;
  onClose: () => void;
  onRevoked: () => void;
}) {
  const [data, setData] = useState<{
    patient: PatientPublic;
    anfragen: KrankmeldungAnfrage[];
    threads: FrageThread[];
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pushing, setPushing] = useState(false);
  const [terminOpen, setTerminOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [openThread, setOpenThread] = useState<string | null>(null);

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await getPatient(patientId);
      setData({ patient: r.patient, anfragen: r.anfragen, threads: r.threads });
    } catch (err: any) {
      setError(err.message ?? 'Patient-Daten konnten nicht geladen werden.');
      console.error('[Patient-Detail]', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [patientId]);

  const onTerminRuf = async () => {
    if (!data) return;
    setPushing(true);
    try {
      const r = await terminRufPatient(patientId);
      if (r.ok) alert('Push-Benachrichtigung wurde gesendet.');
      else alert('Push konnte nicht zugestellt werden. Hat der Patient die App-Benachrichtigungen aktiviert?');
    } catch (err: any) {
      alert('Fehler: ' + err.message);
    } finally {
      setPushing(false);
    }
  };

  const onRevoke = async () => {
    if (!data) return;
    if (!confirm(`Akte von ${data.patient.vorname} ${data.patient.nachname} sperren?\n\nDie Akte wird sofort unzugänglich und die personenbezogenen Daten werden anonymisiert. Diese Aktion ist unumkehrbar.`)) return;
    try {
      await revokePatient(patientId);
      onRevoked();
    } catch (err: any) {
      alert('Fehler: ' + err.message);
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-card">
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>Lädt…</div>
        ) : error || !data ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#C62828' }}>
            <strong>Fehler beim Laden</strong>
            <p style={{ fontSize: 13, marginTop: 8 }}>{error ?? 'Unbekannter Fehler'}</p>
            <button className="btn-primary" onClick={reload} style={{ marginTop: 12 }}>↻ Erneut versuchen</button>
            <button className="btn-ghost" onClick={onClose} style={{ marginLeft: 8 }}>Schließen</button>
          </div>
        ) : (
          <>
            <div className="modal-header">
              <div>
                <h2 style={{ margin: 0 }}>{data.patient.vorname} {data.patient.nachname}</h2>
                <div className="muted small">geb. {data.patient.geburtsdatum}</div>
              </div>
              <button className="btn-icon" onClick={onClose}>×</button>
            </div>

            <div className="modal-body">
              <section>
                <h3>Stammdaten</h3>
                <div className="kv">
                  <div className="k">E-Mail</div><div className="v">{data.patient.email}</div>
                  <div className="k">Akte angelegt</div><div className="v">{new Date(data.patient.created_at).toLocaleString('de-DE')}</div>
                  <div className="k">Consent erteilt</div><div className="v">{new Date(data.patient.consent_at).toLocaleString('de-DE')} ({data.patient.consent_version})</div>
                  <div className="k">Push aktiviert</div><div className="v">{data.patient.has_push ? '✅ Ja' : '❌ Nein'}</div>
                </div>
                <div className="row" style={{ marginTop: 12, flexWrap: 'wrap' }}>
                  <button className="btn-primary" onClick={() => setTerminOpen((v) => !v)}>
                    📅 Termin vorschlagen
                  </button>
                  <button
                    className="btn-secondary"
                    onClick={onTerminRuf}
                    disabled={pushing || !data.patient.has_push}
                    title={!data.patient.has_push ? 'Patient hat Push nicht aktiviert' : 'Push: "Sie können in die Praxis kommen"'}
                  >
                    {pushing ? 'Sende…' : '🔔 "Bereit"-Ruf'}
                  </button>
                  <button className="btn-secondary" onClick={() => setEditOpen(true)}>
                    ✏ Bearbeiten / sperren
                  </button>
                  <button className="btn-danger" onClick={onRevoke}>Akte löschen</button>
                </div>
                {data.patient.chat_banned && (
                  <div style={{
                    marginTop: 12, padding: 10, background: '#FFEBEE',
                    borderLeft: '4px solid #C62828', borderRadius: 6, fontSize: 13,
                  }}>
                    <strong>🚫 Patient ist vom Chat gesperrt.</strong>
                    {data.patient.chat_ban_reason ? <><br />Grund: {data.patient.chat_ban_reason}</> : null}
                  </div>
                )}
                {data.patient.praxis_notiz && (
                  <div style={{
                    marginTop: 8, padding: 10, background: '#F4FAFA',
                    borderLeft: '4px solid var(--primary)', borderRadius: 6, fontSize: 13,
                  }}>
                    <strong>Notiz:</strong> {data.patient.praxis_notiz}
                  </div>
                )}
                {terminOpen && (
                  <PatientTerminForm
                    patientId={data.patient.id}
                    patientName={`${data.patient.vorname} ${data.patient.nachname}`}
                    onClose={() => setTerminOpen(false)}
                    onSent={() => { setTerminOpen(false); /* daten neu laden für Termin-Liste in der UI später */ }}
                  />
                )}
                {editOpen && (
                  <PatientEditForm
                    patient={data.patient}
                    onClose={() => setEditOpen(false)}
                    onSaved={(updated) => {
                      setData({ ...data, patient: updated });
                      setEditOpen(false);
                      reload();
                    }}
                  />
                )}
              </section>

              <section>
                <h3>Krankmeldungen ({data.anfragen.length})</h3>
                {data.anfragen.length === 0 ? (
                  <p className="muted">Noch keine Krankmeldungs-Anfragen.</p>
                ) : (
                  <ul className="anfrage-list">
                    {data.anfragen.map((a) => (
                      <li key={a.id}>
                        <div className="anfrage-head">
                          <strong>{a.zeitraum.von} – {a.zeitraum.bis}</strong>
                          <span className={`status status-${a.status}`}>{a.status}</span>
                        </div>
                        <div className="muted small">{a.beschwerde}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section>
                <h3>Chat-Verläufe ({data.threads.length})</h3>
                {data.threads.length === 0 ? (
                  <p className="muted">Noch keine Frage-Threads.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {data.threads.map((t) => {
                      const isOpen = openThread === t.id;
                      return (
                        <div key={t.id} style={{
                          border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden',
                        }}>
                          <button
                            type="button"
                            onClick={() => setOpenThread(isOpen ? null : t.id)}
                            style={{
                              width: '100%', padding: '10px 14px', background: '#F4FAFA',
                              border: 0, textAlign: 'left', cursor: 'pointer',
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600 }}>{t.titel}</div>
                              <div style={{ fontSize: 11, color: '#5F6B68', marginTop: 2 }}>
                                {t.messages.length} Nachricht{t.messages.length === 1 ? '' : 'en'} · zuletzt {new Date(t.updated_at).toLocaleString('de-DE')}
                              </div>
                            </div>
                            <span className={`status status-${t.status}`}>{t.status}</span>
                            <span style={{ marginLeft: 8, color: '#5F6B68' }}>{isOpen ? '▲' : '▼'}</span>
                          </button>
                          {isOpen && (
                            <div style={{ padding: 12, background: '#fff', borderTop: '1px solid var(--border)' }}>
                              {t.messages.map((m) => (
                                <div key={m.id} className={`msg msg-${m.from}`} style={{ marginBottom: 8 }}>
                                  {m.attachments && m.attachments.length > 0 && (
                                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 4 }}>
                                      {m.attachments.map((aid) => (
                                        <AuthImage key={aid} id={aid} alt="Foto" style={{
                                          maxWidth: 120, maxHeight: 120, borderRadius: 6,
                                        }} />
                                      ))}
                                    </div>
                                  )}
                                  {m.text && <div className="msg-bubble">{m.text}</div>}
                                  <div className="msg-meta">
                                    {m.from === 'patient' ? 'Patient' : 'Praxis'} · {new Date(m.ts).toLocaleString('de-DE')}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
